import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes, signIn } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { BrowserRouter } from 'react-router-dom';
import awsConfig from './aws-config';
import AppShell from './app/AppShell';
import { ToastProvider } from './app/ToastProvider';
import { C3DataProvider } from './data';
import { rememberReturnPath } from './app/returnPath';
import { WorkspaceProvider } from './workspace';

Amplify.configure(awsConfig);

interface User {
  username: string;
  email?: string;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          await checkUser();
          break;
        case 'signInWithRedirect_failure':
          setError('Sign in failed. Please try again.');
          setLoading(false);
          break;
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkUser();
    return () => unsubscribe();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      let email = currentUser.username;
      try {
        const attributes = await fetchUserAttributes();
        email = attributes.email || currentUser.username;
      } catch {
        try {
          const session = await fetchAuthSession();
          email = (session.tokens?.idToken?.payload?.email as string) || currentUser.username;
        } catch {}
      }
      setUser({ username: currentUser.username, email });
      setError(null);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      setError(null);
      // Google sends the person back to `/`; keep the link they came with (a shared incident, say).
      rememberReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      await signInWithRedirect({ provider: 'Google' });
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
      setLoading(false);
    }
  }

  async function handlePasswordSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoggingIn(true);
    setError(null);
    try {
      const result = await signIn({
        username: email,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' }
      });
      if (result.isSignedIn) {
        await checkUser();
      } else {
        setError('Additional authentication required.');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
    }
    setLoggingIn(false);
  }

  async function handleSignOut() {
    try {
      await signOut({ global: true });
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  if (loading) {
    return (
      <div data-theme="original" className="min-h-screen bg-c3-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-c3-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-c3-mute font-c3">Loading Arbitium...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      // Kept exactly as it was before the redesign (frozen palette in src/ui/tokens.css).
      <div data-theme="original" className="min-h-screen bg-c3-bg flex items-center justify-center overflow-hidden font-c3">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-c3-bg via-c3-panel to-c3-bg"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-arbitium-primary/30 rounded-full blur-3xl pulse-glow"></div>
          <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-arbitium-accent/20 rounded-full blur-3xl pulse-glow" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-arbitium-secondary/10 rounded-full blur-3xl pulse-glow" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative z-10 bg-arbitium-dark/70 backdrop-blur-2xl border border-arbitium-primary/20 rounded-3xl p-12 max-w-md w-full mx-4 text-center shadow-2xl shadow-arbitium-primary/10">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-arbitium-primary to-arbitium-accent rounded-2xl blur-xl opacity-50"></div>
              <div className="relative w-full h-full bg-arbitium-darker rounded-2xl flex items-center justify-center border border-arbitium-primary/30">
                <svg viewBox="0 0 186 169" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" preserveAspectRatio="xMidYMid meet" className="w-20 h-20">
                  <g>
                    <g id="svg_11">
                      <polygon className="cls-2" points="86.82009506225586,0 94.82009506225586,0 7.999992370605469,168.95008087158203 0,168.95008087158203 86.82009506225586,0 " fill="#575ba8" id="svg_12"/>
                      <polygon className="cls-2" points="100.4701042175293,0 108.4701042175293,0 21.650012969970703,168.95008087158203 13.64999771118164,168.95008087158203 100.4701042175293,0 " fill="#575ba8" id="svg_13"/>
                      <polygon className="cls-2" points="113.88009262084961,0 121.88009262084961,0 35.06003189086914,168.95008087158203 27.060016632080078,168.95008087158203 113.88009262084961,0 " fill="#575ba8" id="svg_14"/>
                      <polygon className="cls-2" points="134.5900993347168,0 132.5100975036621,4.039997100830078 128.61010360717773,11.629993438720703 126.15009689331055,16.41999053955078 121.7100944519043,25.069992065429688 119.31010055541992,29.720001220703125 114.99009323120117,38.14002227783203 112.40009689331055,43.17002868652344 108.18009567260742,51.38005065917969 93.27009201049805,80.40008544921875 89.92010116577148,86.92008209228516 88.02009201049805,90.6100845336914 84.68009567260742,97.1100845336914 82.58010482788086,101.20008087158203 79.23009872436523,107.72008514404297 77.0900993347168,111.88007354736328 73.7401008605957,118.39008331298828 47.76005935668945,168.95008087158203 39.76004409790039,168.95008087158203 117.94010543823242,16.839988708496094 118.02009201049805,16.679988861083984 126.5900993347168,0 134.5900993347168,0 " fill="#575ba8" id="svg_15"/>
                      <polygon className="cls-2" points="186.11010360717773,160.12007904052734 186.11010360717773,168.12007904052734 73.7401008605957,118.39008331298828 77.0900993347168,111.88007354736328 186.11010360717773,160.12007904052734 " fill="#575ba8" id="svg_16"/>
                      <polygon className="cls-2" points="186.11010360717773,146.80008697509766 186.11010360717773,154.80008697509766 79.23009872436523,107.72008514404297 82.58010482788086,101.20008087158203 186.11010360717773,146.80008697509766 " fill="#575ba8" id="svg_17"/>
                      <polygon className="cls-2" points="186.11010360717773,134.67008209228516 186.11010360717773,142.67008209228516 84.68009567260742,97.1100845336914 88.02009201049805,90.6100845336914 186.11010360717773,134.67008209228516 " fill="#575ba8" id="svg_18"/>
                      <polygon className="cls-2" points="186.11010360717773,121.38007354736328 186.11010360717773,129.38008880615234 89.92010116577148,86.92008209228516 93.27009201049805,80.40008544921875 128.37009811401367,95.89008331298828 138.37009811401367,100.3100814819336 144.75010299682617,103.12007904052734 154.78010177612305,107.55008697509766 160.68009567260742,110.16008758544922 170.68009567260742,114.57007598876953 176.12009811401367,116.97008514404297 186.11010360717773,121.38007354736328 " fill="#575ba8" id="svg_19"/>
                      <polygon className="cls-2" points="186.11010360717773,121.38007354736328 176.12009811401367,116.97008514404297 128.61010360717773,11.629993438720703 132.5100975036621,4.039997100830078 186.11010360717773,121.38007354736328 " fill="#575ba8" id="svg_20"/>
                      <polygon className="cls-2" points="117.88009262084961,16.70998764038086 118.02009201049805,16.679988861083984 117.94010543823242,16.839988708496094 117.88009262084961,16.70998764038086 " fill="#575ba8" id="svg_21"/>
                      <polygon className="cls-2" points="170.68009567260742,114.57007598876953 160.68009567260742,110.16008758544922 121.7100944519043,25.069992065429688 126.15009689331055,16.41999053955078 170.68009567260742,114.57007598876953 " fill="#575ba8" id="svg_22"/>
                      <polygon className="cls-2" points="154.78010177612305,107.55008697509766 144.75010299682617,103.12007904052734 114.99009323120117,38.14002227783203 119.31010055541992,29.720001220703125 154.78010177612305,107.55008697509766 " fill="#575ba8" id="svg_23"/>
                      <polygon className="cls-2" points="138.37009811401367,100.3100814819336 128.37009811401367,95.89008331298828 108.18009567260742,51.38005065917969 112.40009689331055,43.17002868652344 138.37009811401367,100.3100814819336 " fill="#575ba8" id="svg_24"/>
                    </g>
                  </g>
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold gradient-text mb-2">arbitium</h1>
            <p className="text-gray-400">Autonomous Security Platform</p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {!showPasswordLogin ? (
            <>
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-white text-gray-800 font-semibold py-3.5 px-6 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-100 transition-all mb-4 shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-arbitium-primary/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-arbitium-dark text-gray-500">or</span>
                </div>
              </div>
              
              <button
                onClick={() => setShowPasswordLogin(true)}
                className="w-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-semibold py-3.5 px-6 rounded-xl hover:opacity-90 transition-all shadow-lg shadow-arbitium-primary/30"
              >
                Sign in with Email
              </button>
            </>
          ) : (
            <form onSubmit={handlePasswordSignIn}>
              <div className="mb-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-arbitium-darker border border-arbitium-primary/30 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-arbitium-primary transition-colors"
                  required
                />
              </div>
              <div className="mb-6">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-arbitium-darker border border-arbitium-primary/30 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-arbitium-primary transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-semibold py-3.5 px-6 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 mb-4 shadow-lg shadow-arbitium-primary/30"
              >
                {loggingIn ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordLogin(false)}
                className="w-full text-gray-400 hover:text-white transition-colors"
              >
                ← Back to options
              </button>
            </form>
          )}
          
          <p className="text-gray-500 text-sm mt-6">
            Authorized users only
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <WorkspaceProvider>
        <C3DataProvider>
          <ToastProvider>
            <AppShell user={user} onSignOut={handleSignOut} />
          </ToastProvider>
        </C3DataProvider>
      </WorkspaceProvider>
    </BrowserRouter>
  );
}

export default App;
